import React from 'react';
import { Animated, TouchableWithoutFeedback, View, TouchableOpacity } from 'react-native';

import { PercentageIndicator, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { Colors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import {
  map as _map,
  maxBy as _maxBy,
  isUndefined as _isUndefined,
  isFunction as _isFunction,
  isNull as _isNull,
  get as _get,
} from 'lodash';

import { getThemeColors } from '../../../../Themes';
import BillingTestKeys from '../../../../Utils/BillingTestKeys.js';
import styles from '../BarChart.Styles';

import BarChartDetails from './BarChartDetails';

const NEGATIVE_BILL_BAR_HEIGHT = 28;

/**
 * Function will create function, which returns color and style for bar in graph.
 * Result colors and style depends on themeColors.
 *
 */
const createBarStyleGetter =
  themeColors =>
    ({ isLastOutLined, isLastMonth, currentActiveIndex, index, billTypes, billDetails }) => {

      let customColor = (billTypes && billTypes.length > 0
        && billTypes.find(el => el?.id === billDetails?.billType)) ?
        billTypes.find(el => el?.id === billDetails?.billType).color : undefined

      // calculate fill color
      let fillColor = customColor ? customColor : themeColors.inputIconColor;
      if (isLastOutLined && isLastMonth) {
        fillColor = Colors.transparent;
      } else if (!_isUndefined(currentActiveIndex) && index !== currentActiveIndex) {
        fillColor = themeColors.fillColor;
      }

      // border color
      let borderColor = customColor ? customColor : themeColors.inputIconColor;

      if (
        isLastOutLined &&
        isLastMonth &&
        !_isUndefined(currentActiveIndex) &&
        index !== currentActiveIndex
      ) {
        borderColor = themeColors.fillColor;
      }

      return {
        fillColor,
        style: {
          borderColor,
          borderWidth: isLastOutLined && isLastMonth ? 1 : undefined,
        },
      };
    };

const getMaxValue = (data) => {
  let maxValue = 0
  data?.forEach(billPerMonth => {
    billPerMonth?.data?.forEach(bill => {
      if (bill?.value > maxValue) {
        maxValue = bill?.value
      }
    })
  })

  return {
    value: maxValue
  }

}

function Bars({
  data,
  onBarDetailsPress,
  onBillBarPress,
  isLastOutLined,
  currentActiveIndex,
  getBillAnimatedStyle,
  theme,
  yearStyle,
  showLastMonthTimePeriodOnly,
  getBarStyle,
  barChartDetailsContainerStyle,
  billTypes,
}) {
  const localBarStyler = getBarStyle ? getBarStyle : createBarStyleGetter(theme && theme.colors);

  // get max value in data array
  const max = getMaxValue(data) || {};

  return _map(data, (bills, index) => {

    const isYearVisible =
      (index === 0 || (index !== 0 && data[index].data[0]?.year !== data[index - 1]?.data[0]?.year)) &&
      bills?.data[0]?.monthNumber === '1';

    const isLastMonth = index === data.length - 1;
    const billAnimatedStyle = (_isFunction(getBillAnimatedStyle) && getBillAnimatedStyle(index)) || {};

    return (
      <TouchableWithoutFeedback onPress={() => onBillBarPress(index)}>
        <View>
          <Animated.View
            style={[
              styles.billsPerMonthContainer(bills?.data.length),
              billAnimatedStyle.marginRightAnimatedStyle,
              isYearVisible ? styles.yearMargin : {},
            ]}
          >
            {
              bills?.data.map((billDetails, i) => {
                const billDetailsValue = _get(billDetails, 'value', 0);
                const height = max.value ? (billDetailsValue / max.value) * 100 : 0;

                const isNegativeBill = billDetails.value < 0;
                const billingColors = getThemeColors(theme.name)?.colors;
                const isAvailableBill = !_isNull(billDetails.value);

                const { fillColor: barColor, style: barStyle } = localBarStyler({
                  isLastOutLined,
                  isLastMonth,
                  currentActiveIndex,
                  index,
                  billTypes: billTypes?.data,
                  billDetails
                });
                return (
                  <View key={`Bar#${i}`} style={styles.singleBarViewStyle}>
                    <Animated.View
                      testID={`BarID${i}`}
                      style={styles.singleBarViewStyle}
                    >
                      {/* Increase the press area of the bars for usability */}
                      {!isAvailableBill ? null : (
                        <TouchableWithoutFeedback onPress={() => onBillBarPress(index)}>
                          <View style={styles.pressAreaStyle} />
                        </TouchableWithoutFeedback>
                      )}

                      {isYearVisible && i === 0 && (
                        <TouchableOpacity
                          onPress={() => { }}
                          style={styles.yearStyleContainer}
                          activeOpacity={1}
                        >
                          <VFText
                            style={[styles.yearStyle(billingColors), yearStyle]}
                            testKey={BillingTestKeys.barsYearBillDetails}
                          >
                            {billDetails.year}
                          </VFText>
                        </TouchableOpacity>
                      )}

                      <TouchableWithoutFeedback onPress={() => onBillBarPress(index)}>
                        {billDetails.value === 0 ? (
                          <View style={styles.zeroBillContainer}>
                            <View
                              style={[
                                styles.zeroBill,
                                !_isUndefined(currentActiveIndex) &&
                                i !== currentActiveIndex &&
                                styles.zeroBillDisabled,
                              ]}
                            />
                          </View>
                        ) : (
                          <PercentageIndicator
                            type="vertical"
                            borderRadius={6}
                            value={"100"}
                            color={barColor}
                            fillColor={"transparent"}
                            animate
                            delay={50 * (data.length - 1 - i)}
                            duration={500}
                            animationType="scaleUp"
                            style={barStyle}
                            testID={BillingTestKeys.barsPercentageIndicator}
                            containerStyle={{ height: isNegativeBill ? NEGATIVE_BILL_BAR_HEIGHT : height }}
                          />
                        )}
                      </TouchableWithoutFeedback>
                    </Animated.View>
                  </View>
                );
              })
            }

          </Animated.View >
          {
            !_isUndefined(currentActiveIndex) && index === currentActiveIndex && (
              <BarChartDetails
                billsPerMonth={bills}
                onBarDetailsPress={onBarDetailsPress}
                animatedStyle={billAnimatedStyle.opacityAnimatedStyle}
                showTimePeriod={!showLastMonthTimePeriodOnly || isLastMonth}
                containerStyle={barChartDetailsContainerStyle}
                billTypes={billTypes?.data || []}
                currentActiveIndex={currentActiveIndex}
                isYearVisible={isYearVisible}
              />
            )
          }
        </View>
      </TouchableWithoutFeedback>
    )
  });
}

export default withTheme(Bars);
